import { useMediaQuery } from '@mui/material';
import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState
} from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridColumn as Column,
  GridExpandChangeEvent,
  GridSortChangeEvent,
  GridToolbar
} from '@progress/kendo-react-grid';
import { sortBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { breakpoints } from 'src/assets/_responsive-mixin';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { useColumns } from 'src/components/shared/DataGrid/ColumnMenu';
import { ComboBoxFilterCell } from 'src/components/shared/DataGrid/ComboBoxFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { DateCell } from 'src/components/shared/DataGrid/DateCell';
import { selectCustomers } from 'src/store/customers/customers.selectors';
import { selectGrades } from 'src/store/grades/grades.selectors';
import { selectPackagings } from 'src/store/packagings/packagings.selectors';
import { selectPallets } from 'src/store/pallets/pallets.selectors';
import { selectSegments } from 'src/store/segments/segments.selectors';
import {
  SpecificationDetailDto,
  SpecificationDto,
  SpecificationParameterDto
} from 'src/store/specifications/specification';
import { SpecificationApi } from 'src/store/specifications/specifications.api';
import {
  getSpecificationColumns,
  SpecificationsListColumnMenu
} from './SpecificationsListColumnMenu';
import { SpecificationListMobile } from './SpecificationsListMobile';

type SpecificationsListProps = {
  data: DataResult;
  loading: boolean;
  requestState: DataSourceRequestState;
  onEdit: (specification: SpecificationDto) => void;
  onSortChange: (event: GridSortChangeEvent) => void;
  onFilterChange: (filter: CompositeFilterDescriptor | undefined) => void;
  onResetFilters: () => void;
  onPageChange: (skip: number, take: number) => void;
  initExporter: (exporter: ExcelExport) => void;
  onDelete: (spec: SpecificationDto) => void;
};

export const SpecificationsList = (props: SpecificationsListProps) => {
  const {
    data,
    loading,
    requestState,
    onEdit,
    onSortChange,
    onFilterChange,
    onResetFilters,
    onPageChange,
    initExporter,
    onDelete
  } = props;

  const { t } = useTranslation();

  const largeScreen = useMediaQuery(`(min-width: ${breakpoints.large})`);

  const [currentSpecification, setCurrentSpecification] =
    useState<Nullable<SpecificationDetailDto>>(null);

  const [specifications, setSpecifications] = useState<DataResult>({
    data: [],
    total: 0
  } as DataResult);

  const { columns, isColumnVisible, onColumnsChange } =
    useColumns<SpecificationDto>(getSpecificationColumns());

  const segments$ = useSelector(selectSegments);
  const grades$ = useSelector(selectGrades);
  const customers$ = useSelector(selectCustomers);
  const packagings$ = useSelector(selectPackagings);
  const pallets$ = useSelector(selectPallets);

  useEffect(() => {
    setSpecifications(data);
  }, [data]);

  useEffect(() => {
    setSpecifications({
      ...specifications,
      data: specifications.data.map(specification =>
        specification.id === currentSpecification?.id
          ? {
              ...specification,
              expanded: true,
              productParameters: sortBy(
                currentSpecification?.specificationParameters ?? [],
                specificationParameter =>
                  specificationParameter.parameterName?.toLowerCase(),
                'parameterName'
              )
            }
          : specification
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpecification]);

  const onSpecificationDetailClick = async (event: GridExpandChangeEvent) => {
    if (event.dataItem.expanded) {
      setSpecifications({
        ...specifications,
        data: specifications.data.map(specification =>
          specification.id === event.dataItem.id
            ? {
                ...specification,
                expanded: false
              }
            : specification
        )
      });
    } else {
      const response = await SpecificationApi.getById(event.dataItem.id);
      if (response.ok) {
        setCurrentSpecification(response.data);
      }
    }
  };

  return largeScreen ? (
    <ExcelExport
      data={data.data}
      ref={initExporter}
      // Setter opp hvordan excel-filen skal se ut
      columns={[
        ...(isColumnVisible(columns, 'no')
          ? [{ field: 'no', title: 'Nr' }]
          : []),
        ...(isColumnVisible(columns, 'name')
          ? [{ field: 'name', title: 'Navn' }]
          : []),
        ...(isColumnVisible(columns, 'regDate')
          ? [{ field: 'regDate', title: 'Dato' }]
          : []),
        ...(isColumnVisible(columns, 'segmentName')
          ? [{ field: 'segmentName', title: 'Segment' }]
          : []),
        ...(isColumnVisible(columns, 'gradeName')
          ? [{ field: 'gradeName', title: 'Grad' }]
          : []),
        ...(isColumnVisible(columns, 'customerName')
          ? [{ field: 'customerName', title: 'Kunde' }]
          : []),
        ...(isColumnVisible(columns, 'packagingName')
          ? [{ field: 'packagingName', title: 'Emballasje' }]
          : []),
        ...(isColumnVisible(columns, 'palletName')
          ? [{ field: 'palletName', title: 'Palletype' }]
          : []),
        ...(isColumnVisible(columns, 'active')
          ? [{ field: 'active', title: 'Aktiv', width: 160 }]
          : [])
      ]}
    >
      <DataGrid
        data={specifications}
        loading={loading}
        pageable
        pageSize={20}
        filter={requestState.filter}
        sort={requestState.sort}
        skip={requestState.skip}
        take={requestState.take}
        onSortChange={onSortChange}
        onFilterChange={event => {
          onFilterChange(event.filter);
        }}
        onPageChange={onPageChange}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        expandField="expanded"
        detail={props => (
          <SpecificationParameters
            parameters={props.dataItem.productParameters}
          />
        )}
        onExpandChange={onSpecificationDetailClick}
      >
        <GridToolbar>
          <SpecificationsListColumnMenu
            selectedColumns={columns}
            onSelectedColumnsChange={onColumnsChange}
          />
          <SecondaryButton
            label="Nullstill filter"
            name="resetFilters"
            className="ml-8"
            onClick={onResetFilters}
            disabled={
              !requestState.filter || requestState.filter.filters.length === 0
            }
          />
        </GridToolbar>
        {isColumnVisible(columns, 'no') && (
          <Column field="no" title={t('props.number')} width="200px" />
        )}
        {isColumnVisible(columns, 'name') && (
          <Column field="name" title={t('props.name')} />
        )}
        {isColumnVisible(columns, 'regDate') && (
          <Column
            field="regDate"
            title={t('props.date')}
            filterable={false}
            cell={props => <DateCell {...props} />}
          />
        )}
        {isColumnVisible(columns, 'segmentName') && (
          <Column
            field="segmentName"
            title={t('segment.singular')}
            filterCell={props => (
              <ComboBoxFilterCell
                {...props}
                options={segments$}
                optionField="name"
              />
            )}
          />
        )}
        {isColumnVisible(columns, 'gradeName') && (
          <Column
            field="gradeName"
            title={t('grade.singular')}
            filterCell={props => (
              <ComboBoxFilterCell
                {...props}
                options={grades$}
                optionField="name"
              />
            )}
          />
        )}
        {isColumnVisible(columns, 'customerName') && (
          <Column
            field="customerName"
            title={t('customer.singular')}
            filterCell={props => (
              <ComboBoxFilterCell
                {...props}
                options={customers$}
                optionField="name"
              />
            )}
          />
        )}
        {isColumnVisible(columns, 'packagingName') && (
          <Column
            field="packagingName"
            title={t('packaging.singular')}
            filterCell={props => (
              <ComboBoxFilterCell
                {...props}
                options={packagings$}
                optionField="name"
              />
            )}
          />
        )}
        {isColumnVisible(columns, 'palletName') && (
          <Column
            field="palletName"
            title={t('pallet.singular')}
            filterCell={props => (
              <ComboBoxFilterCell
                {...props}
                options={pallets$}
                optionField="name"
              />
            )}
          />
        )}
        {isColumnVisible(columns, 'active') && (
          <Column
            width="160px"
            field="active"
            title={t('props.active')}
            cell={props => <BooleanCell {...props} />}
            filterCell={props => <BooleanFilterCell {...props} />}
          />
        )}
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>
    </ExcelExport>
  ) : (
    <SpecificationListMobile {...props} />
  );
};

const SpecificationParameters: React.FC<{
  parameters: SpecificationParameterDto[];
}> = ({ parameters }) => {
  const { t } = useTranslation();

  return (
    <DataGrid items={parameters} filterable={false} style={{ marginTop: 20 }}>
      <Column
        field="parameterName"
        title={t('parameter.singular')}
        width="220px"
      />
      <Column field="methodName" title={t('method.singular')} width="220px" />
      <Column
        field="minProd"
        title={t('specification.minProd')}
        format="{0:0.000}"
      />
      <Column
        field="maxProd"
        title={t('specification.maxProd')}
        format="{0:0.000}"
      />
      <Column field="decProd" title={t('specification.decProd')} />
      <Column
        field="minCustomer"
        title={t('specification.minCustomer')}
        format="{0:0.000}"
      />
      <Column
        field="maxCustomer"
        title={t('specification.maxCustomer')}
        format="{0:0.000}"
      />
      <Column field="decCustomer" title={t('specification.decCustomer')} />
      <Column field="user.initials" title={t('user.initials')} />
    </DataGrid>
  );
};
